import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Play, Trophy, Target, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { OptimizationService } from '@/services/OptimizationService';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { AnalysisMode } from '@/types/trading-analysis-types';

interface ThresholdResult {
  threshold: number;
  accuracy: number;
  precision: number;
  recall: number;
  totalPredictions: number;
  skipRate: number;
  recommendationScore: number;
  isOptimal?: boolean;
}

interface MultiThresholdOptimizerProps {
  selectedFile: TradingFile | null;
  selectedAnalysisMode: AnalysisMode;
  maxSequenceLength: number;
}

const DEFAULT_THRESHOLDS = [0.50, 0.52, 0.55, 0.58, 0.60, 0.65, 0.70];

export const MultiThresholdOptimizer: React.FC<MultiThresholdOptimizerProps> = ({
  selectedFile,
  selectedAnalysisMode,
  maxSequenceLength
}) => {
  const [selectedThresholds, setSelectedThresholds] = useState<number[]>(DEFAULT_THRESHOLDS);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ThresholdResult[]>([]);
  const [bestThreshold, setBestThreshold] = useState<ThresholdResult | null>(null);

  const handleThresholdToggle = (threshold: number, checked: boolean) => {
    if (checked) {
      setSelectedThresholds(prev => [...prev, threshold].sort());
    } else {
      setSelectedThresholds(prev => prev.filter(t => t !== threshold));
    }
  };

  const handleOptimize = async () => {
    if (!selectedFile?.id || selectedThresholds.length === 0) {
      toast.error('Please select a file and at least one threshold');
      return;
    }

    setIsOptimizing(true);
    setProgress(0);
    setResults([]);
    setBestThreshold(null);

    try {
      const result = await OptimizationService.runMultiThresholdOptimization({
        fileId: selectedFile.id,
        thresholds: selectedThresholds,
        maxSequenceLength,
        analysisMode: selectedAnalysisMode
      }, (progressUpdate) => {
        setProgress((progressUpdate.completed / progressUpdate.total) * 100);
      });

      if (result.success && result.data) {
        setResults(result.data.results);
        setBestThreshold(result.data.bestThreshold);
        toast.success(`Optimization complete! Best threshold: ${(result.data.bestThreshold.threshold * 100).toFixed(0)}%`);
      } else {
        toast.error(result.error || 'Optimization failed');
      }
    } catch (error) {
      console.error('Optimization error:', error);
      toast.error('Failed to run optimization');
    } finally {
      setIsOptimizing(false);
      setProgress(0);
    }
  };

  const renderThresholdSelection = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Threshold Selection
        </CardTitle>
        <CardDescription>
          Select win thresholds to test and compare
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
          {DEFAULT_THRESHOLDS.map((threshold) => (
            <div key={threshold} className="flex items-center space-x-2">
              <Checkbox
                id={`threshold-${threshold}`}
                checked={selectedThresholds.includes(threshold)}
                onCheckedChange={(checked) => handleThresholdToggle(threshold, checked as boolean)}
              />
              <Label htmlFor={`threshold-${threshold}`} className="text-sm">
                {(threshold * 100).toFixed(0)}%
              </Label>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-muted-foreground">
            {selectedThresholds.length} thresholds selected
          </div>
          <Button 
            onClick={handleOptimize} 
            disabled={isOptimizing || !selectedFile || selectedThresholds.length === 0}
          >
            <Play className="h-4 w-4 mr-2" />
            {isOptimizing ? 'Optimizing...' : 'Start Optimization'}
          </Button>
        </div>

        {isOptimizing && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Testing thresholds...</span>
              <span>{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderResults = () => {
    if (results.length === 0) return null;

    const chartData = results.map(result => ({
      threshold: `${(result.threshold * 100).toFixed(0)}%`,
      accuracy: result.accuracy,
      predictions: result.totalPredictions,
      skipRate: result.skipRate
    }));

    return (
      <div className="space-y-6">
        {/* Best Threshold Card */}
        {bestThreshold && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                Best Threshold Recommendation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {(bestThreshold.threshold * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Threshold</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{bestThreshold.accuracy.toFixed(1)}%</div>
                  <div className="text-sm text-muted-foreground">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{bestThreshold.totalPredictions}</div>
                  <div className="text-sm text-muted-foreground">Predictions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{bestThreshold.skipRate.toFixed(1)}%</div>
                  <div className="text-sm text-muted-foreground">Skip Rate</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accuracy Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Accuracy by Threshold</CardTitle>
            <CardDescription>
              Comparison of prediction accuracy across different win thresholds
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="threshold" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      name === 'accuracy' ? `${value.toFixed(1)}%` : value,
                      name === 'accuracy' ? 'Accuracy' : 'Predictions'
                    ]}
                  />
                  <Bar 
                    dataKey="accuracy" 
                    fill="hsl(var(--primary))" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Results Table */}
        <Card>
          <CardHeader>
            <CardTitle>Detailed Results</CardTitle>
            <CardDescription>
              Complete comparison of all tested thresholds
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Threshold</TableHead>
                  <TableHead>Accuracy</TableHead>
                  <TableHead>Precision</TableHead>
                  <TableHead>Recall</TableHead>
                  <TableHead>Predictions</TableHead>
                  <TableHead>Skip Rate</TableHead>
                  <TableHead>Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results
                  .sort((a, b) => b.recommendationScore - a.recommendationScore)
                  .map((result, index) => (
                  <TableRow key={result.threshold} className={index === 0 ? 'bg-primary/5' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {(result.threshold * 100).toFixed(0)}%
                        {index === 0 && (
                          <Badge variant="default" className="text-xs">
                            <Trophy className="h-3 w-3 mr-1" />
                            Best
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={result.accuracy >= 60 ? 'default' : 'secondary'}>
                        {result.accuracy.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    <TableCell>{result.precision.toFixed(1)}%</TableCell>
                    <TableCell>{result.recall.toFixed(1)}%</TableCell>
                    <TableCell>{result.totalPredictions}</TableCell>
                    <TableCell>{result.skipRate.toFixed(1)}%</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {result.recommendationScore.toFixed(0)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (!selectedFile) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-lg font-medium mb-2">No File Selected</p>
        <p className="text-sm text-muted-foreground">
          Please select a trading file to run threshold optimization
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Multi-Threshold Optimization</h3>
          <p className="text-sm text-muted-foreground">
            Test multiple win thresholds to find the optimal configuration
          </p>
        </div>
        <Badge variant="outline">File: {selectedFile.filename}</Badge>
      </div>

      {renderThresholdSelection()}
      {renderResults()}
    </div>
  );
};